import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './header.scss';
import UserIcon from '../../assets/dark-theme/icons/user-yellow.png';
import logout from '../../assets/dark-theme/icons/Logout.svg';
import { PageRoutesToModule } from '../../components/shared/modules';
import { useSelector } from 'react-redux';
import { RootState } from '../../state/features/reducers';
import { decodeToken } from '../../utils/tokenUtils';
import {
    ConfigProviderComponent,
    antdTheme,
} from '../../components/shared';
import { documentation } from '../../assets/dark-theme';
import { useSharedContext } from '../../components/shared/ContextProvider';
import { useTranslation } from 'react-i18next';
import useChangeLanguage from '../../hooks/useChangeLanguage';
// Ant Design-
import { Dropdown, Tooltip, message } from 'antd';
import {
    CustomerServiceOutlined,
    GlobalOutlined,
    MenuUnfoldOutlined,
    MenuFoldOutlined,
    KeyOutlined,
    SettingOutlined,
} from '@ant-design/icons';

interface HeaderProps {
    onLogoutClick?: () => void;
    onToggle?: () => void;
    mobileCollapse: boolean;
}
interface DecodedToken {
    email?: string;
    [key: string]: any;
}

const baseProductImageClass = 'productIcon svg';

const Header: React.FC<HeaderProps> = ({
    onLogoutClick = () => {},
    onToggle = () => {},
    mobileCollapse,
}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const user = useSelector(
        (state: RootState) => state.auth.user || {},
    );
    const { sharedContext, handleContextChange } = useSharedContext();
    const { t, i18n } = useTranslation();
    const changeLanguage = useChangeLanguage();

    // Extract the breadcrumb from the path
    const breadCrumbArr = location?.pathname?.split('/') ?? [];

    // Determine if we're on a run details page
    const isRunDetails =
        breadCrumbArr.includes('runs') &&
        breadCrumbArr.includes('facility');

    // Use the appropriate breadcrumb based on the page type
    let breadCrumb: string, pageTitle: string, module: string;

    if (isRunDetails) {
        // For run details pages, use 'runs' as the base breadcrumb
        breadCrumb = 'runs';

        // Check if we have a specific submenu selected (from sharedContext)
        const runConfig =
            PageRoutesToModule[
                breadCrumb as keyof typeof PageRoutesToModule
            ];
        if (sharedContext?.currentMenu) {
            const subMenuConfig =
                PageRoutesToModule[
                    sharedContext.currentMenu as keyof typeof PageRoutesToModule
                ];
            pageTitle =
                (runConfig as any).title +
                ((subMenuConfig as any)?.title || '');
        } else {
            pageTitle = (runConfig as any).title;
        }

        module = (runConfig as any).module;
    } else {
        // For all other pages, use the last segment of the path
        breadCrumb =
            breadCrumbArr?.[breadCrumbArr?.length - 1] ?? 'dashboard';
        const moduleConfig =
            PageRoutesToModule[
                breadCrumb as keyof typeof PageRoutesToModule
            ];

        pageTitle = breadCrumb;
        module = 'basic';

        if (moduleConfig && 'title' in moduleConfig) {
            // Only include submenu if we're on a run details page
            pageTitle = moduleConfig.title;
            module = moduleConfig.module;
        }
    }

    // Clear the current menu when navigating away from run details
    useEffect(() => {
        // If we're not on a run details page and there's a current menu set, clear it
        if (!isRunDetails && sharedContext?.currentMenu) {
            handleContextChange({
                ...sharedContext,
                currentMenu: undefined,
            });
        }
    }, [
        location.pathname,
        isRunDetails,
        sharedContext,
        handleContextChange,
    ]);

    const moduleIcon = `${baseProductImageClass} ${module}`;
    let email = '';

    if (user?.idToken) {
        const userData = decodeToken(user?.idToken) as DecodedToken;
        if (userData?.email) email = userData.email;
    }

    const handleAccessTokenClick = () => {
        if (user?.accessToken) {
            navigator.clipboard
                .writeText(user.accessToken)
                .then(() => {
                    message.success(
                        t('header.accessTokenCopied'),
                    );
                })
                .catch(() => {
                    message.error(t('header.accessTokenCopyFailed'));
                });
        } else {
            message.warning(t('header.noAccessToken'));
        }
    };

    const handleProfileClick = () => {
        navigate('/profile');
    };

    let apiDocsUrl = '';
    try {
        const strippedAPIURL = new URL(user.apiURL!)?.origin; // Extract the base URL from the API URL
        const customerApiBase =
            user?.customerApiBase || strippedAPIURL;
        apiDocsUrl = customerApiBase + '/swagger/';
    } catch (error) {
        console.error('Error extracting API URL:', error);
    }

    const items: any[] = [
        {
            label: (
                <a
                    href="https://customerdocs.liveline.cloud/index.html"
                    target={'_blank'}
                    rel="noreferrer"
                >
                    <div className="profileMenuItem">
                        <img
                            src={documentation}
                            alt={t('header.documentation')}
                            className="menuIcon"
                        />
                        <span className="menuName">
                            {' '}
                            {t('header.documentation')}
                        </span>
                    </div>
                </a>
            ),
            key: '0',
        },
        {
            label: (
                <a
                    href="https://liveline.atlassian.net/servicedesk/customer/portals"
                    target={'_blank'}
                    rel="noreferrer"
                >
                    <div className="profileMenuItem">
                        <CustomerServiceOutlined
                            style={{ fontSize: 16 }}
                            className="menuIcon"
                        />
                        <span className="menuName"> {t('header.contactUs')}</span>
                    </div>
                </a>
            ),
            key: '1',
        },
        {
            label: (
                <div
                    className="profileMenuItem"
                    onClick={handleProfileClick}
                >
                    <SettingOutlined
                        style={{ fontSize: 16 }}
                        className="menuIcon"
                    />
                    <span className="menuName">{t('header.accountSettings')}</span>
                </div>
            ),
            key: '2',
        },
        {
            label: (
                <div
                    className="profileMenuItem"
                    onClick={handleAccessTokenClick}
                >
                    <KeyOutlined
                        style={{ fontSize: 16 }}
                        className="menuIcon"
                    />
                    <span className="menuName">
                        {t('header.copyAccessToken')}
                    </span>
                </div>
            ),
            key: '3',
        },
        {
            label: (
                <a
                    href={apiDocsUrl}
                    target={'_blank'}
                    rel="noreferrer"
                >
                    <div className="profileMenuItem">
                        <span className="menuName">
                            {' '}
                            {t('header.apiDocumentation')}
                        </span>
                    </div>
                </a>
            ),
            key: '4',
        },
        {
            label: (
                <div className="profileMenuItem">
                    <GlobalOutlined
                        style={{ fontSize: 16 }}
                        className="menuIcon"
                    />
                    <span className="menuName">{t('header.language')}</span>
                </div>
            ),
            key: 'language',
            children: [
                { label: 'English',        key: 'lang-en'    },
                { label: 'Español (MX)',   key: 'lang-es'    },
                { label: 'Español (ES)',   key: 'lang-es-ES' },
                { label: 'Deutsch',        key: 'lang-de'    },
                { label: 'Français',       key: 'lang-fr'    },
                { label: 'Polski',         key: 'lang-pl'    },
                { label: 'Português (BR)', key: 'lang-pt-BR' },
                { label: '中文',            key: 'lang-zh'    },
            ].map(item => ({
                key: item.key,
                label: (
                    <span style={item.key === `lang-${i18n.language}` ? { color: '#006B8C' } : undefined}>
                        {item.label}
                    </span>
                ),
            })),
        },
        {
            label: (
                <div
                    className="profileMenuItem"
                    onClick={onLogoutClick}
                >
                    <img
                        src={logout}
                        className="menuIcon"
                        title={t('header.logout')}
                        alt={t('header.userIconAlt')}
                    />
                    <span className="menuName">{t('header.logout')}</span>
                </div>
            ),
            key: '5',
        },
    ];

    // Controls for the show/hide of the left nav menu-
    const [tooltipVisible, setTooltipVisible] = useState(false);
    const IconComponent = mobileCollapse
        ? MenuUnfoldOutlined
        : MenuFoldOutlined;
    const tooltipText = mobileCollapse ? t('header.showMenu') : t('header.hideMenu');

    return (
        <div className="headerContainer">
            <Tooltip
                title={tooltipText}
                open={tooltipVisible}
                onOpenChange={setTooltipVisible}
                key={mobileCollapse ? 'closed' : 'open'}
                placement="right"
            >
                <IconComponent
                    style={{ fontSize: 20, cursor: 'pointer' }}
                    onClick={() => {
                        onToggle();
                        setTooltipVisible(false); // Hide tooltip on toggle-
                    }}
                />
            </Tooltip>
            <div className="headerTitle">
                {moduleIcon && (
                    <span
                        className={`${moduleIcon} moduleIcon`}
                        title={module}
                    >
                        &nbsp;&nbsp;
                    </span>
                )}
                <span className="currentPage">{`${pageTitle}`}</span>
            </div>
            <div className="userName profileUserName">{email}</div>
            <div className="headerUserProfile">
                <div className="userIcon">
                    <ConfigProviderComponent theme={antdTheme}>
                        <Dropdown
                            menu={{
                                items,
                                onClick: ({ key }) => {
                                    if (key.startsWith('lang-')) {
                                        changeLanguage(key.replace('lang-', ''));
                                    }
                                },
                            }}
                            overlayClassName="profileDropdown"
                            trigger={['click']}
                            placement="bottomRight"
                            arrow
                        >
                            <img
                                src={UserIcon}
                                className="userImg"
                                title={email}
                                alt="user icon"
                            />
                        </Dropdown>
                    </ConfigProviderComponent>
                </div>
            </div>
        </div>
    );
};

export default Header;
